import { ReactNode, createContext } from "react";
import useCusto, { CustoType, CreateCustoType, EditCustoType, CreateCustosComContaPagarRequest } from "./hooks/useCusto";

interface CustoContextProviderProps {
    children: ReactNode;
}

export interface CustoContextData {
    createCusto: (createCustoData: CreateCustoType) => Promise<CreateCustoType | null>;
    getCustoPaged: (
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        IsFinancial?: boolean,
        pageSize?: number
    ) => Promise<CustoType[] | null>;
    updateCusto: (editCustoData: EditCustoType) => Promise<EditCustoType | null>;
    deleteCusto: (custoId: number) => Promise<CustoType | null>;
    setCusto: any;
    custo: CustoType;
    listCusto: CustoType[];
    initialListCusto: CustoType[];
    setListCusto: (custo: CustoType[]) => void;
    setCustoPageCount: (count: number) => void;
    custoPageCount: number;

    getCustoFinanceiroPaged: (
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        pageSize?: number
    ) => Promise<CustoType[] | null>;
    listCustoFinanceiro: CustoType[];
    setListCustoFinanceiro: (custo: CustoType[]) => void;
    initialListCustoFinanceiro: CustoType[];
    setInitialListCustoFinanceiro: (custo: CustoType[]) => void;
    custoFinanceiroPageCount: number;
    setCustoFinanceiroPageCount: (count: number) => void;

    getCustoGeralPaged: (
        pageNumber: number,
        ContratoId?: number,
        EmpresaId?: number,
        pageSize?: number
    ) => Promise<CustoType[] | null>;
    listCustoGeral: CustoType[];
    setListCustoGeral: (custo: CustoType[]) => void;
    initialListCustoGeral: CustoType[];
    setInitialListCustoGeral: (custo: CustoType[]) => void;
    custoGeralPageCount: number;
    setCustoGeralPageCount: (count: number) => void;

    exportCustoBytes: (ContratoId?: number, EmpresaId?: number, IsFinancial?: boolean) => Promise<void>;

    totaisFinanceiro: { totalValor: number; totalSaldo: number; saldoFinal: number };
    totaisGeral: { totalValor: number; totalSaldo: number; saldoFinal: number };
    getCustoTotais: (
        ContratoId?: number,
        EmpresaId?: number,
        IsFinancial?: boolean
    ) => Promise<{ totalValor: number; totalSaldo: number } | null>;

    enviarCustoParaFinanceiro: (custoId: number) => Promise<boolean>;
    createCustosWithContaPagar: (
        payload: CreateCustosComContaPagarRequest
    ) => Promise<any[] | null>;

    getCustoAttachment: (custoId: number) => void;
}

const initialState: CustoContextData = {
    createCusto: async () => null,
    getCustoPaged: async () => null,
    updateCusto: async () => null,
    deleteCusto: async () => null,
    setCusto: () => { },
    custo: {
        id: 0,
        description: "",
        proportionalCurrentStudents: false,
        individualValue: 0,
        currentBalance: 0,
        requestDate: "",
        deliveryDate: "",
        paymentDate: "",
        receiver: "",
        observation: "",
        isPaid: false,
        empresaId: 0,
        contratoId: 0,
        tipoCustoId: 0,
        tipoCustoName: "",
        tipoCustoIsFinancial: false,
        hasReceiptAttachment: false,
    },
    listCusto: [],
    initialListCusto: [],
    setListCusto: () => { },
    setCustoPageCount: () => { },
    custoPageCount: 0,

    getCustoFinanceiroPaged: async () => null,
    listCustoFinanceiro: [],
    setListCustoFinanceiro: () => { },
    initialListCustoFinanceiro: [],
    setInitialListCustoFinanceiro: () => { },
    custoFinanceiroPageCount: 0,
    setCustoFinanceiroPageCount: () => { },

    getCustoGeralPaged: async () => null,
    listCustoGeral: [],
    setListCustoGeral: () => { },
    initialListCustoGeral: [],
    setInitialListCustoGeral: () => { },
    custoGeralPageCount: 0,
    setCustoGeralPageCount: () => { },

    exportCustoBytes: async () => { },

    totaisFinanceiro: { totalValor: 0, totalSaldo: 0, saldoFinal: 0 },
    totaisGeral: { totalValor: 0, totalSaldo: 0, saldoFinal: 0 },
    getCustoTotais: async () => null,

    enviarCustoParaFinanceiro: async () => false,
    createCustosWithContaPagar: async () => null,

    getCustoAttachment: () => {},
};

export const CustoContext =
    createContext<CustoContextData>(initialState);

export default function CustoContextProvider({
    children,
}: CustoContextProviderProps) {
    const {
        createCusto,
        getCustoPaged,
        updateCusto,
        deleteCusto,
        custo,
        listCusto,
        initialListCusto,
        setListCusto,
        setCustoPageCount,
        custoPageCount,

        getCustoFinanceiroPaged,
        listCustoFinanceiro,
        setListCustoFinanceiro,
        custoFinanceiroPageCount,
        setCustoFinanceiroPageCount,
        initialListCustoFinanceiro,
        setInitialListCustoFinanceiro,

        getCustoGeralPaged,
        listCustoGeral,
        setListCustoGeral,
        custoGeralPageCount,
        setCustoGeralPageCount,
        initialListCustoGeral,
        setInitialListCustoGeral,

        exportCustoBytes,

        totaisFinanceiro,
        totaisGeral,
        getCustoTotais,

        enviarCustoParaFinanceiro,
        createCustosWithContaPagar,

        getCustoAttachment,
    } = useCusto();

    return (
        <CustoContext.Provider
            value={{
                createCusto,
                getCustoPaged,
                updateCusto,
                deleteCusto,
                setCusto: () => { },
                custo,
                listCusto,
                initialListCusto,
                setListCusto,
                setCustoPageCount,
                custoPageCount,

                getCustoFinanceiroPaged,
                listCustoFinanceiro,
                setListCustoFinanceiro,
                custoFinanceiroPageCount,
                setCustoFinanceiroPageCount,
                initialListCustoFinanceiro,
                setInitialListCustoFinanceiro,

                getCustoGeralPaged,
                listCustoGeral,
                setListCustoGeral,
                custoGeralPageCount,
                setCustoGeralPageCount,
                initialListCustoGeral,
                setInitialListCustoGeral,

                exportCustoBytes,

                totaisFinanceiro,
                totaisGeral,
                getCustoTotais,

                enviarCustoParaFinanceiro,
                createCustosWithContaPagar,

                getCustoAttachment,
            }}
        >
            {children}
        </CustoContext.Provider>
    );
}
